import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ConsultationProvider } from '../contexts/ConsultationContext';
import { ConsultationDashboard } from '../components/ConsultationDashboard';
import ErrorBoundary from '../components/ErrorBoundary';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  LogOut, Search, Calendar, User, Clock, Phone, Hash, 
  Stethoscope, Filter, ChevronRight, RefreshCw, CreditCard, 
  HeartPulse, UserPlus, UserCheck, Shield, Activity, ShoppingCart, X, Package, FileText, Building2, CheckCircle, AlertTriangle, Microscope, Settings, ListChecks, MoreHorizontal, IndianRupee
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { formatDateIST, formatDateTimeIST } from '../utils/date';
import { formatDoctorName } from '../utils/doctor';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { PastVisitDrawer } from '../components/PastVisitDrawer';
import PreviousVisitPrefillModal from '../components/PreviousVisitPrefillModal';
import { AppointmentDetailDrawer } from '../components/AppointmentDetailDrawer';
import DoctorQueueWidget from '../components/DoctorQueueWidget';
import GlobalPatientSearch from '../components/GlobalPatientSearch';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function toProperCase(str) {
  if (!str) return '';
  return str.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
}

const DATE_FILTERS = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'week', label: 'This Week' },
  { key: 'lastweek', label: 'Last Week' },
  { key: 'month', label: 'This Month' },
  { key: 'lastmonth', label: 'Last Month' },
  { key: '3months', label: 'Last 3 Months' },
  { key: '6months', label: 'Last 6 Months' },
  { key: 'all', label: 'All' },
  { key: 'custom', label: 'Custom' }
];

const VISIT_TYPES = [
  { key: 'OP', label: 'OP' },
  { key: 'IP', label: 'IP' },
  { key: 'ALL', label: 'All' }
];

const STATUS_COLORS = {
  'Tentative': { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', dot: 'bg-purple-500' },
  'Checked In': { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', dot: 'bg-blue-500' },
  'In Visit': { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-500' },
  'Completed': { bg: 'bg-emerald-50', text: 'text-emerald-800', border: 'border-emerald-200', dot: 'bg-emerald-600' },
  'Cancelled': { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', dot: 'bg-red-500' },
  // Legacy raw-status keys kept for any list/card that hasn't migrated yet.
  'tentative': { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', dot: 'bg-purple-500' },
  'checked-in': { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', dot: 'bg-blue-500' },
  'in-visit': { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-500' },
  'completed': { bg: 'bg-emerald-50', text: 'text-emerald-800', border: 'border-emerald-200', dot: 'bg-emerald-600' },
  'cancelled': { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', dot: 'bg-red-500' },
  'default': { bg: 'bg-gray-50', text: 'text-gray-700', border: 'border-gray-200', dot: 'bg-gray-400' }
};

// Canonical 4+1 user-visible buckets (Tentative · Checked In · In Visit ·
// Completed · Cancelled). Always rendered as filter chips; counts default
// to 0 when missing and the chip greys out so the doctor sees the entire
// pipeline at a glance.
const STANDARD_STAGES = [
  { key: 'Tentative', label: 'Tentative' },
  { key: 'Checked In', label: 'Checked In' },
  { key: 'In Visit', label: 'In Visit' },
  { key: 'Completed', label: 'Completed' },
  { key: 'Cancelled', label: 'Cancelled' },
];

export default function DashboardPage() {
  const { user, logout } = useAuth();
  const [appointments, setAppointments] = useState([]);
  // Capability flag — doctors only see "Register Patient" when the tenant
  // (or this specific doctor) has reception powers enabled. Admin always
  // sees it because they own the clinic.
  const [canReception, setCanReception] = useState(false);
  // Inventory button is hidden when pharmacy_mode='full' since the legacy
  // PharmacyPage already owns the inventory workflow there.
  const [showInventory, setShowInventory] = useState(false);
  useEffect(() => {
    const role = (user?.role || '').toLowerCase();
    if (role === 'admin' || role === 'receptiondesk') { setCanReception(true); }
    else if (role !== 'doctor') { setCanReception(false); setShowInventory(false); return; }
    axios.get(`${API_URL}/api/clinic-ops/me/capabilities`, { withCredentials: true })
      .then(({ data }) => {
        if (role === 'doctor') setCanReception(!!data?.can_reception);
        setShowInventory((data?.pharmacy_mode || 'lite') === 'lite');
      })
      .catch(() => { setCanReception(false); setShowInventory(false); });
  }, [user?.role]);
  const [statusCounts, setStatusCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [activeConsultation, setActiveConsultation] = useState(null);
  // Returning-patient prefill prompt — populated when we detect a prior
  // consultation with rx/tests for this patient. Cleared on close.
  const [previousVisit, setPreviousVisit] = useState(null);
  const [prefillReloadKey, setPrefillReloadKey] = useState(0);

  // Filters
  const [dateFilter, setDateFilter] = useState('today');
  const [visitType, setVisitType] = useState('OP');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [specFilter, setSpecFilter] = useState('ALL');
  const [specialisations, setSpecialisations] = useState([]);
  const [searchMrn, setSearchMrn] = useState('');
  const [searchName, setSearchName] = useState('');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [tumorBoardOnly, setTumorBoardOnly] = useState(false);
  const [showPurchaseRequests, setShowPurchaseRequests] = useState(false);
  const [purchaseRequests, setPurchaseRequests] = useState([]);
  const [prLoading, setPrLoading] = useState(false);
  const [prSearch, setPrSearch] = useState('');
  const [ipPatients, setIpPatients] = useState([]);
  // IP card click → drawer. We reuse AppointmentDetailDrawer (built for
  // OP) because it already renders sections / discharge summary / past
  // visits / lab tests — exactly what doctors need for an IP review.
  const [ipDrawerApptId, setIpDrawerApptId] = useState(null);
  const [ipCounts, setIpCounts] = useState({ admitted: 0, discharged: 0, total: 0 });
  const [ipLoading, setIpLoading] = useState(false);
  const [ipFilter, setIpFilter] = useState('admitted');

  // Guards against stale responses overwriting newer filter results
  const apptReqIdRef = useRef(0);
  const apptAbortRef = useRef(null);
  const ipReqIdRef = useRef(0);
  const ipAbortRef = useRef(null);

  const loadPurchaseRequests = useCallback(async () => {
    setPrLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/purchase-requests`, { withCredentials: true });
      setPurchaseRequests(data || []);
    } catch (e) { /* ignore */ }
    setPrLoading(false);
  }, []);

  // Expiring stock alert (amber chip in header)
  const [expiringSummary, setExpiringSummary] = useState(null);
  const [showExpiringDrawer, setShowExpiringDrawer] = useState(false);
  const [expiringLoading, setExpiringLoading] = useState(false);
  const [expiringSearch, setExpiringSearch] = useState('');

  // Past visit drawer (opened from card chips)
  const [pastVisitId, setPastVisitId] = useState(null);
  const [pastVisitTitle, setPastVisitTitle] = useState('');

  useEffect(() => {
    axios.get(`${API_URL}/api/inventory/expiring-alerts?days=30`, { withCredentials: true })
      .then(({ data }) => setExpiringSummary(data))
      .catch(() => {});
  }, []);

  const openExpiringDrawer = useCallback(async () => {
    setShowExpiringDrawer(true);
    setExpiringLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/inventory/expiring-alerts?days=30`, { withCredentials: true });
      setExpiringSummary(data);
    } catch (e) { /* ignore */ }
    setExpiringLoading(false);
  }, []);

  // Load specialisations
  useEffect(() => {
    const loadSpecs = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/appointments/specialisations`, { withCredentials: true });
        setSpecialisations(data || []);
      } catch (e) { /* ignore */ }
    };
    loadSpecs();
  }, []);

  const [lastSyncedAt, setLastSyncedAt] = useState(null);

  const loadAppointments = useCallback(async ({ silent = false } = {}) => {
    // Cancel any in-flight request and bump the generation so a late response
    // from a previous filter selection can't overwrite the new one.
    if (apptAbortRef.current) apptAbortRef.current.abort();
    const controller = new AbortController();
    apptAbortRef.current = controller;
    const reqId = ++apptReqIdRef.current;

    // Silent refreshes (auto-poll, post-edit reloads) MUST NOT collapse the
    // dashboard back to the "Loading appointments..." placeholder — that
    // wipes scroll position, hides cards mid-interaction, and feels broken.
    // Only show the full loader when there are no cards yet (initial load).
    if (!silent) setLoading(true);
    try {
      const params = { date_filter: dateFilter, visit_type: visitType };
      if (statusFilter !== 'ALL') params.status = statusFilter;
      if (specFilter !== 'ALL') params.specialisation = specFilter;
      if (searchMrn.trim()) params.mrn = searchMrn.trim();
      if (searchName.trim()) params.patient_name = searchName.trim();
      if (dateFilter === 'custom') {
        if (customFrom) params.date_from = customFrom;
        if (customTo) params.date_to = customTo;
      }
      if (tumorBoardOnly) params.tumor_board_only = true;

      const { data } = await axios.get(`${API_URL}/api/appointments`, {
        params,
        withCredentials: true,
        signal: controller.signal,
      });
      if (reqId !== apptReqIdRef.current) return; // stale response, ignore
      setAppointments(data.appointments || []);
      setStatusCounts(data.status_counts || {});
      setLastSyncedAt(new Date());
    } catch (e) {
      if (axios.isCancel?.(e) || e.name === 'CanceledError' || e.code === 'ERR_CANCELED') return;
      if (reqId !== apptReqIdRef.current) return;
      console.error('Load appointments error:', e);
    } finally {
      if (reqId === apptReqIdRef.current && !silent) setLoading(false);
    }
  }, [dateFilter, visitType, statusFilter, specFilter, searchMrn, searchName, customFrom, customTo, tumorBoardOnly]);

  useEffect(() => {
    loadAppointments();
  }, [loadAppointments]);

  // Auto-refresh the appointments list every 30 seconds so newly-registered
  // patients show up for the doctor without a manual refresh. Always silent —
  // the existing list and scroll position are preserved while the data updates
  // in place. Pauses when the browser tab is hidden to save resources.
  useEffect(() => {
    let timer = null;
    const start = () => {
      if (timer) return;
      timer = setInterval(() => {
        if (!document.hidden) loadAppointments({ silent: true });
      }, 30000);
    };
    const stop = () => {
      if (timer) { clearInterval(timer); timer = null; }
    };
    const onVisibility = () => {
      if (document.hidden) {
        stop();
      } else {
        loadAppointments({ silent: true });
        start();
      }
    };
    start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => { stop(); document.removeEventListener('visibilitychange', onVisibility); };
  }, [loadAppointments]);

  // Load IP patients when IP tab active
  const loadIpPatients = useCallback(async () => {
    if (ipAbortRef.current) ipAbortRef.current.abort();
    const controller = new AbortController();
    ipAbortRef.current = controller;
    const reqId = ++ipReqIdRef.current;

    setIpLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/ip-patients`, {
        params: { filter: ipFilter },
        withCredentials: true,
        signal: controller.signal,
      });
      if (reqId !== ipReqIdRef.current) return;
      setIpPatients(data.patients || []);
      setIpCounts(data.counts || { admitted: 0, discharged: 0, total: 0 });
    } catch (e) {
      if (axios.isCancel?.(e) || e.name === 'CanceledError' || e.code === 'ERR_CANCELED') return;
      if (reqId !== ipReqIdRef.current) return;
      console.error(e);
    } finally {
      if (reqId === ipReqIdRef.current) setIpLoading(false);
    }
  }, [ipFilter]);

  useEffect(() => {
    if (visitType === 'IP') loadIpPatients();
  }, [visitType, ipFilter, loadIpPatients]);

  // Open patient consultation
  const openPatient = async (appt) => {
    try {
      // Check for existing consultation or create new
      const { data: consultations } = await axios.get(`${API_URL}/api/consultations`, {
        params: { patient_id: appt.patient_id },
        withCredentials: true
      });

      // Reuse any existing consultation that matches this specific appointment
      // (regardless of status) so doctors always come back to the same record
      // for a given appointment — no data is ever "lost" on navigation.
      const existingShallow = consultations.find(c =>
        c.appointment_id && appt.appointment_id && c.appointment_id === appt.appointment_id
      );
      let consultation = existingShallow;
      if (existingShallow) {
        // The list endpoint strips `sections` to keep its payload small. We
        // hydrate the full doc here so the prefill-suppression guard below
        // can inspect prescription / investigations items — otherwise a
        // re-opened consultation that already has rx looks empty and the
        // modal would spam every reopen.
        try {
          const { data: full } = await axios.get(
            `${API_URL}/api/consultations/${existingShallow.id}`,
            { withCredentials: true }
          );
          consultation = full;
        } catch (e) {
          // Fall back to the shallow doc; the worst case is one
          // duplicate-modal flash, not a data corruption.
        }
      }
      if (!consultation) {
        const { data: newConsultation } = await axios.post(`${API_URL}/api/consultations`, {
          patient_id: appt.patient_id,
          patient_name: appt.patient_name,
          appointment_id: appt.appointment_id || '',
          patient_age: appt.age || null,
          patient_gender: appt.gender || '',
          ip_number: appt.ip_number || '',
          // Surfacing the queue entry id lets the backend pull the
          // receptionist's pre-consult triage (vitals + chief complaint)
          // onto the new consultation — closing the reception→doctor loop.
          queue_entry_id: appt.queue_entry_id || null,
        }, { withCredentials: true });
        consultation = newConsultation;
        if (newConsultation?.triage_applied) {
          const t = newConsultation.triage_applied;
          const bits = [];
          if (t.vitals_count) bits.push(`${t.vitals_count} vital${t.vitals_count === 1 ? '' : 's'}`);
          if (t.had_chief_complaint) bits.push('chief complaint');
          if (bits.length) {
            toast.success(`Pre-filled from reception triage: ${bits.join(' + ')}`);
          }
        }
      } else if (appt.queue_entry_id) {
        // Existing consultation reopened from the queue — attempt to merge
        // any newly-recorded triage onto sections that the doctor hasn't
        // touched yet. Safe: backend skips sections that already have
        // content. Hydrate the full doc afterwards so the UI reflects
        // the merge.
        try {
          const { data: applied } = await axios.post(
            `${API_URL}/api/consultations/${consultation.id}/apply-triage`,
            null,
            { params: { queue_entry_id: appt.queue_entry_id }, withCredentials: true }
          );
          if (applied?.applied) {
            const bits = [];
            if (applied.vitals_count) bits.push(`${applied.vitals_count} vital${applied.vitals_count === 1 ? '' : 's'}`);
            if (applied.had_chief_complaint) bits.push('chief complaint');
            if (bits.length) toast.success(`Updated from reception triage: ${bits.join(' + ')}`);
            // Re-fetch the consultation so the in-memory copy carries the merged sections.
            try {
              const { data: hydrated } = await axios.get(
                `${API_URL}/api/consultations/${consultation.id}`,
                { withCredentials: true }
              );
              consultation = hydrated;
            } catch (_) { /* keep stale; merge is in DB anyway */ }
          }
        } catch (_) { /* triage merge is best-effort */ }
      }

      setSelectedPatient(appt);
      setActiveConsultation(consultation);

      // If this consultation was opened from the reception queue widget,
      // flip the queue entry from 'waiting' → 'in_consult' so the
      // receptionist sees who's currently with the doctor. Fire-and-
      // forget — UI must not block on this.
      if (appt.queue_entry_id) {
        axios.post(
          `${API_URL}/api/clinic-ops/queue/${appt.queue_entry_id}/call`,
          {},
          { withCredentials: true }
        ).catch(() => { /* silent — queue status is observability, not critical path */ });
      }

      // Returning-patient hint — fetch the most-recent prior consultation
      // for this patient (any doctor in this clinic). The modal only
      // surfaces ONCE per browser-session per consultation so doctors don't
      // get spammed every time they reopen the same patient mid-session.
      // sessionStorage key intentionally scoped to the consultation id, so
      // a real new consultation (new id) will re-surface the prompt.
      const seenKey = `prefill-shown-${consultation.id}`;
      let alreadySeen = false;
      try { alreadySeen = window.sessionStorage.getItem(seenKey) === '1'; } catch (_) {}
      if (alreadySeen) {
        return;
      }
      try {
        const { data: prev } = await axios.get(
          `${API_URL}/api/consultations/previous-visit-prefill`,
          {
            params: {
              patient_id: appt.patient_id,
              exclude_consultation_id: consultation.id,
            },
            withCredentials: true,
          }
        );
        if (prev) {
          setPreviousVisit(prev);
          try { window.sessionStorage.setItem(seenKey, '1'); } catch (_) {}
        }
      } catch (e) {
        // Silent — prefill is a convenience, not a blocker.
      }
    } catch (e) {
      console.error('Open patient error:', e);
    }
  };

  // Back to list
  const backToList = () => {
    setSelectedPatient(null);
    setActiveConsultation(null);
    setPreviousVisit(null);
  };

  // Total appointments
  const totalCount = Object.values(statusCounts).reduce((s, c) => s + c, 0);

  // If consultation is active, show it
  if (activeConsultation && selectedPatient) {
    return (
      <ErrorBoundary homeUrl="/dashboard">
        <ConsultationProvider
          key={`${activeConsultation.id}-${prefillReloadKey}`}
          consultationId={activeConsultation.id}
          onBack={backToList}
        >
          <ConsultationDashboard
            selectedPatient={selectedPatient}
          />
          {/* Returning-patient prefill prompt — modal sits on top of the
              consultation page and is dismissed before any edits are made. */}
          <PreviousVisitPrefillModal
            open={!!previousVisit}
            onClose={() => setPreviousVisit(null)}
            consultationId={activeConsultation.id}
            previousVisit={previousVisit}
            currentConsultation={activeConsultation}
            onApplied={() => {
              setPrefillReloadKey((k) => k + 1);
            }}
          />
        </ConsultationProvider>
      </ErrorBoundary>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" data-testid="dashboard-page">
      {/* Top Header */}
      <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-blue-700 flex items-center justify-center">
            <Stethoscope className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-gray-900" style={{ fontFamily: 'Manrope' }}>
              {visitType === 'IP' ? 'Inpatient Dashboard' : visitType === 'ER' ? 'Emergency Dashboard' : 'Outpatient Dashboard'}
            </h1>
            <p className="text-xs text-gray-500">{formatDoctorName(user?.name)} &middot; {user?.specialization}</p>
          </div>
        </div>
        {/* Global Patient Search — header-level, ⌘K to focus */}
        <div className="flex-1 max-w-md mx-6 hidden md:block">
          <GlobalPatientSearch />
        </div>
        <div className="flex items-center gap-2">
          {/* Last sync status — silent auto-refresh every 30s */}
          <button
            onClick={() => loadAppointments({ silent: true })}
            data-testid="last-synced-chip"
            className="flex items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 hover:bg-gray-100 px-2.5 py-1 text-[11px] font-medium text-gray-600 transition"
            title="Click to refresh now"
          >
            <RefreshCw className="h-3 w-3" />
            <span>
              {lastSyncedAt
                ? <>Synced <span className="font-semibold text-gray-800">{formatDateTimeIST(lastSyncedAt)}</span></>
                : 'Syncing...'}
            </span>
          </button>
          {expiringSummary && (expiringSummary.total_count > 0) && (
            <button
              onClick={openExpiringDrawer}
              data-testid="expiring-alert-chip"
              className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold transition ${
                expiringSummary.expired_count > 0
                  ? 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100'
                  : 'bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100'
              }`}
              title={`${expiringSummary.expired_count} expired · ${expiringSummary.expiring_count} expiring within 30 days`}
            >
              <AlertTriangle className="h-3.5 w-3.5" />
              {expiringSummary.expired_count > 0 && (
                <span>{expiringSummary.expired_count} expired</span>
              )}
              {expiringSummary.expired_count > 0 && expiringSummary.expiring_count > 0 && (
                <span className="opacity-60">·</span>
              )}
              {expiringSummary.expiring_count > 0 && (
                <span>{expiringSummary.expiring_count} expiring</span>
              )}
            </button>
          )}
          <Button variant="outline" size="sm" onClick={() => { setShowPurchaseRequests(true); loadPurchaseRequests(); }}
            className="gap-1.5 text-xs" data-testid="purchase-requests-btn">
            <ShoppingCart className="h-3.5 w-3.5" /> Purchase Requests
          </Button>

          {/* Consolidated workspace menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1.5 text-xs" data-testid="dashboard-more-menu">
                <MoreHorizontal className="h-3.5 w-3.5" /> More
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-zinc-500">
                Workspace
              </DropdownMenuLabel>
              {canReception && (
                <DropdownMenuItem onSelect={() => window.location.assign('/reception')} data-testid="dashboard-menu-reception">
                  <UserPlus className="h-4 w-4 mr-2 text-emerald-600" />
                  <div className="flex-1">
                    <div className="text-sm font-medium">Register Patient</div>
                    <div className="text-[10px] text-zinc-500">Reception desk</div>
                  </div>
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onSelect={() => window.location.assign('/reception/roster')} data-testid="dashboard-menu-roster">
                <Calendar className="h-4 w-4 mr-2 text-emerald-700" />
                <div className="flex-1">
                  <div className="text-sm font-medium">Duty Roster</div>
                  <div className="text-[10px] text-zinc-500">My shifts & coverage</div>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => window.location.assign('/inventory')} data-testid="dashboard-menu-inventory">
                <Package className="h-4 w-4 mr-2 text-amber-600" />
                <div className="flex-1">
                  <div className="text-sm font-medium">Inventory</div>
                  <div className="text-[10px] text-zinc-500">Medicines · GST · expiry</div>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => window.location.assign('/financials')} data-testid="dashboard-menu-financials">
                <IndianRupee className="h-4 w-4 mr-2 text-emerald-700" />
                <div className="flex-1">
                  <div className="text-sm font-medium">My Financials</div>
                  <div className="text-[10px] text-zinc-500">Fees collected & pharmacy sales</div>
                </div>
              </DropdownMenuItem>
              {(user?.role || '').toLowerCase() === 'admin' && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-zinc-500">Admin</DropdownMenuLabel>
                  <DropdownMenuItem onSelect={() => window.location.assign('/admin/overview')} data-testid="dashboard-menu-admin">
                    <Settings className="h-4 w-4 mr-2 text-blue-700" />
                    <div className="flex-1">
                      <div className="text-sm font-medium">Admin Portal</div>
                      <div className="text-[10px] text-zinc-500">Users · branding · billing</div>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => window.location.assign('/management')} data-testid="dashboard-menu-management">
                    <Activity className="h-4 w-4 mr-2 text-violet-700" />
                    <div className="flex-1">
                      <div className="text-sm font-medium">Management Cockpit</div>
                      <div className="text-[10px] text-zinc-500">KPIs · AI insights · PDF</div>
                    </div>
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          <Button variant="ghost" size="sm" onClick={logout} className="gap-2 text-gray-500" data-testid="logout-btn">
            <LogOut className="h-4 w-4" /> Sign Out
          </Button>
        </div>
      </header>

      {/* Filter Bar */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 space-y-3 flex-shrink-0">
        {/* Row 1: Date filter + Visit type + Refresh */}
        <div className="flex items-center gap-3 flex-wrap">
          {/* Date filter pills — visually muted while Tumor Board mode is on
              since TB mode bypasses the date window server-side. */}
          <div
            className={`flex items-center gap-1 bg-gray-100 rounded-lg p-0.5 transition-opacity ${tumorBoardOnly ? 'opacity-40 pointer-events-none' : ''}`}
            data-testid="date-filter"
            title={tumorBoardOnly ? 'Disabled while Tumor Board mode is active' : ''}
          >
            {DATE_FILTERS.map(f => (
              <button key={f.key} onClick={() => setDateFilter(f.key)}
                disabled={tumorBoardOnly}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                  dateFilter === f.key ? 'bg-white shadow-sm text-blue-800' : 'text-gray-500 hover:text-gray-700'
                }`} data-testid={`date-${f.key}`}>
                {f.label}
              </button>
            ))}
          </div>

          {/* Visit type — same muting rule as date filters under TB mode. */}
          <div
            className={`flex items-center gap-1 bg-gray-100 rounded-lg p-0.5 transition-opacity ${tumorBoardOnly ? 'opacity-40 pointer-events-none' : ''}`}
            data-testid="visit-type-filter"
            title={tumorBoardOnly ? 'Disabled while Tumor Board mode is active' : ''}
          >
            {VISIT_TYPES.map(v => (
              <button key={v.key} onClick={() => setVisitType(v.key)}
                disabled={tumorBoardOnly}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                  visitType === v.key ? 'bg-white shadow-sm text-blue-800' : 'text-gray-500 hover:text-gray-700'
                }`} data-testid={`vtype-${v.key}`}>
                {v.label}
              </button>
            ))}
          </div>

          {/* Tumor Board cross-cutting filter — when active, shows every TB
              case for this doctor across ALL dates and visit types so the
              MDT meeting prep view is one click away from any other view. */}
          <button
            type="button"
            onClick={() => setTumorBoardOnly((v) => !v)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold border transition-all ${
              tumorBoardOnly
                ? 'bg-rose-600 text-white border-rose-600 shadow-sm hover:bg-rose-700'
                : 'bg-white text-rose-700 border-rose-200 hover:bg-rose-50'
            }`}
            data-testid="tumor-board-filter"
            title={tumorBoardOnly
              ? 'Showing only Tumor Board cases (any date, any visit type). Click to clear.'
              : 'Show only Tumor Board cases across all dates'}
          >
            <Microscope className="h-4 w-4" />
            Tumor Board
            {tumorBoardOnly && <X className="h-3.5 w-3.5 ml-0.5 opacity-80" />}
          </button>

          <Button variant="ghost" size="sm" onClick={loadAppointments} className="gap-1" data-testid="refresh-btn">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>

          {dateFilter === 'custom' && (
            <div className="flex items-center gap-2">
              <Input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
                className="h-9 text-sm w-36" data-testid="custom-from" />
              <span className="text-gray-400 text-sm">to</span>
              <Input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
                className="h-9 text-sm w-36" data-testid="custom-to" />
            </div>
          )}
        </div>

        {/* Row 2: Search inputs + Specialisation filter */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative w-48">
            <Hash className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input value={searchMrn} onChange={e => setSearchMrn(e.target.value)}
              placeholder="Search MRN..." className="pl-8 h-9 text-sm" data-testid="search-mrn" />
          </div>
          <div className="relative w-56">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input value={searchName} onChange={e => setSearchName(e.target.value)}
              placeholder="Search patient name..." className="pl-8 h-9 text-sm" data-testid="search-name" />
          </div>
          {specialisations.length > 0 && (
            <Select value={specFilter} onValueChange={setSpecFilter}>
              <SelectTrigger className="w-48 h-9 text-sm" data-testid="spec-filter">
                <Stethoscope className="h-4 w-4 mr-1 text-gray-400" />
                <SelectValue placeholder="Specialisation" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Specialisations</SelectItem>
                {specialisations.map(s => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {/* Status tabs + Content */}
      <div className="flex-1 overflow-hidden flex flex-col">
        {visitType === 'IP' ? (
          /* ========== IP PATIENTS VIEW ========== */
          <>
            {/* IP filter tabs */}
            <div className="px-4 pt-3 flex items-center gap-2 flex-shrink-0" data-testid="ip-filter-tabs">
              {[
                { key: 'all', label: 'All', count: ipCounts.total },
                { key: 'admitted', label: 'Currently Admitted', count: ipCounts.admitted },
                { key: 'discharged', label: 'Discharged', count: ipCounts.discharged },
              ].map(f => (
                <button key={f.key} onClick={() => setIpFilter(f.key)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all border ${
                    ipFilter === f.key ? 'bg-blue-50 border-blue-300 text-blue-800' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                  }`} data-testid={`ip-filter-${f.key}`}>
                  {f.label}
                  <Badge className={`text-xs ${ipFilter === f.key ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'}`}>{f.count}</Badge>
                </button>
              ))}
            </div>

            {/* IP patient grid */}
            <div className="flex-1 overflow-auto p-4">
              {ipLoading ? (
                <div className="text-center py-16 text-gray-400">
                  <RefreshCw className="h-8 w-8 mx-auto mb-3 animate-spin" />
                  <p className="text-base">Loading IP patients...</p>
                </div>
              ) : ipPatients.length === 0 ? (
                <div className="text-center py-16 text-gray-400">
                  <HeartPulse className="h-10 w-10 mx-auto mb-3 opacity-40" />
                  <p className="text-lg font-medium">No IP patients found</p>
                  <p className="text-sm mt-1">No inpatients under your care for the selected filter</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                  {ipPatients.map((pt, i) => {
                    const isAdmitted = pt.status === 'admitted';
                    const canOpen = !!pt.appointment_id;
                    return (
                      <button
                        key={pt.ip_number || i}
                        type="button"
                        onClick={() => canOpen && setIpDrawerApptId(pt.appointment_id)}
                        disabled={!canOpen}
                        className={`text-left w-full p-4 rounded-xl border-2 transition-all ${
                          isAdmitted ? 'border-green-200 bg-green-50/30' : 'border-gray-200 bg-gray-50/30'
                        } ${canOpen ? 'hover:shadow-md hover:border-blue-300 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-300' : 'cursor-default opacity-90'}`}
                        title={canOpen ? 'Click to view Rx, tests, discharge summary' : 'Linked appointment unavailable'}
                        data-testid={`ip-card-${i}`}>
                        {/* Status + IP Number + Days */}
                        <div className="flex items-center justify-between mb-2">
                          <Badge className={`text-xs ${isAdmitted ? 'bg-green-100 text-green-700 border-green-200' : 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                            <div className={`w-1.5 h-1.5 rounded-full mr-1.5 ${isAdmitted ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
                            {isAdmitted ? 'Admitted' : 'Discharged'}
                          </Badge>
                          <div className="flex items-center gap-2">
                            {pt.days_admitted && (
                              <Badge className={`text-[10px] border-0 ${pt.days_admitted > 7 ? 'bg-red-100 text-red-700' : pt.days_admitted > 3 ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>
                                <Clock className="h-3 w-3 mr-0.5" /> Day {pt.days_admitted}
                              </Badge>
                            )}
                            <span className="text-xs font-bold text-blue-700">{pt.ip_number}</span>
                          </div>
                        </div>

                        {/* Patient name */}
                        <h3 className="text-base font-bold text-gray-900 mb-0.5" style={{ fontFamily: 'Manrope' }}>
                          {pt.salutation ? `${pt.salutation} ` : ''}{toProperCase(pt.patient_name)}
                        </h3>
                        {(pt.age || pt.gender) && (
                          <p className="text-xs text-gray-500 mb-2">{pt.age ? `${pt.age} yrs` : ''}{pt.age && pt.gender ? ' / ' : ''}{pt.gender || ''}</p>
                        )}

                        {/* Room / Ward / Bed - prominent section */}
                        {(pt.room_info || pt.admitted_to) && (
                          <div className="bg-blue-50 border border-blue-100 rounded-lg p-2.5 mb-2.5">
                            <div className="flex items-start gap-2">
                              <Building2 className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                {pt.room_info ? (
                                  <>
                                    <p className="text-sm font-bold text-blue-800">
                                      Room {pt.room_info.room}{pt.room_info.bed ? ` - Bed ${pt.room_info.bed}` : ''}
                                    </p>
                                    <p className="text-[11px] text-blue-600">
                                      {pt.room_info.floor}{pt.admitted_to ? ` | ${pt.admitted_to}` : ''}
                                    </p>
                                  </>
                                ) : (
                                  <p className="text-sm font-medium text-blue-700">{pt.admitted_to}</p>
                                )}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Details */}
                        <div className="space-y-1 text-sm text-gray-600">
                          <div className="flex items-center gap-2">
                            <Hash className="h-3.5 w-3.5 text-gray-400" />
                            <span>MRN: {pt.mrn}</span>
                          </div>
                          {pt.phone && (
                            <div className="flex items-center gap-2">
                              <Phone className="h-3.5 w-3.5 text-gray-400" />
                              <span>{pt.phone}</span>
                            </div>
                          )}
                          <div className="flex items-center gap-2">
                            <Calendar className="h-3.5 w-3.5 text-green-500" />
                            <span className="text-xs">Admitted: {formatDateTimeIST(pt.date_of_admission)}</span>
                          </div>
                          {pt.date_of_discharge && (
                            <div className="flex items-center gap-2">
                              <Calendar className="h-3.5 w-3.5 text-red-400" />
                              <span className="text-xs">Discharged: {formatDateTimeIST(pt.date_of_discharge)}</span>
                            </div>
                          )}
                        </div>

                        {/* Tags */}
                        <div className="flex flex-wrap items-center gap-1.5 mt-2">
                          {pt.scheme && (
                            <Badge className="bg-teal-100 text-teal-700 text-xs border-0">
                              <Shield className="h-3 w-3 mr-0.5" /> {pt.scheme}
                            </Badge>
                          )}
                          {pt.admitted_for && pt.admitted_for.length > 0 && pt.admitted_for.map((af, j) => (
                            <Badge key={j} className="bg-blue-50 text-blue-700 text-xs border-0">{af}</Badge>
                          ))}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        ) : (
        /* ========== OP/ER PATIENTS VIEW ========== */
        <>
        {/* Status tabs */}
        <div className="px-4 pt-3 flex items-center gap-2 flex-wrap flex-shrink-0" data-testid="status-tabs">
          <button onClick={() => setStatusFilter('ALL')}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all border ${
              statusFilter === 'ALL' ? 'bg-blue-50 border-blue-300 text-blue-800' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
            }`} data-testid="status-ALL">
            All
            <Badge className="bg-blue-100 text-blue-800 text-xs">{totalCount}</Badge>
          </button>
          {STANDARD_STAGES.map(({ key, label }) => {
            // Counts come from the API's status_counts. Statuses absent for
            // the current filters render as greyed-out 0 chips so the doctor
            // sees the complete pipeline at a glance — and can still click
            // to filter (which will simply show an empty list with helpful
            // empty-state messaging).
            const count = statusCounts[key] || 0;
            const colors = STATUS_COLORS[key] || STATUS_COLORS['default'];
            const active = statusFilter === key;
            const empty = count === 0;
            return (
              <button
                key={key}
                onClick={() => setStatusFilter(key)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all border ${
                  active
                    ? `${colors.bg} ${colors.border} ${colors.text}`
                    : empty
                      ? 'bg-white border-gray-100 text-gray-400 hover:border-gray-200'
                      : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                }`}
                data-testid={`status-${key}`}
              >
                <div className={`w-2 h-2 rounded-full ${empty ? 'bg-gray-300' : colors.dot}`} />
                {label}
                <Badge variant="secondary" className={`text-xs ${empty ? 'opacity-50' : ''}`}>
                  {count}
                </Badge>
              </button>
            );
          })}
        </div>

        {/* Patient list */}
        <div className="flex-1 overflow-auto px-4 py-3">
          <DoctorQueueWidget user={user} onOpen={openPatient} />
          {loading ? (
            <div className="text-center py-16 text-gray-400">
              <RefreshCw className="h-8 w-8 mx-auto mb-3 animate-spin" />
              <p className="text-base">Loading appointments...</p>
            </div>
          ) : appointments.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <Calendar className="h-10 w-10 mx-auto mb-3 opacity-40" />
              <p className="text-lg font-medium">No appointments found</p>
              <p className="text-sm mt-1">Try changing the filters or date range</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3">
              {appointments.map((appt, i) => {
                // Prefer the backend-computed display_status (canonical 5
                // buckets) so the card matches the chip filter exactly.
                const displayStatus = appt.display_status || appt.status || '';
                const colors = STATUS_COLORS[displayStatus] || STATUS_COLORS[appt.status] || STATUS_COLORS['default'];
                return (
                  <button key={appt.appointment_id || i}
                    onClick={() => openPatient(appt)}
                    className={`text-left p-3.5 rounded-xl border-2 ${colors.border} ${colors.bg} hover:shadow-md transition-all group min-w-0 w-full`}
                    data-testid={`appt-card-${i}`}>
                    {/* Time row */}
                    {appt.time && (
                      <div className="flex items-center justify-end mb-1.5">
                        <span className="text-[11px] text-gray-500 flex items-center gap-1 whitespace-nowrap">
                          <Clock className="h-3 w-3" /> {formatDateTimeIST(appt.date, appt.time)}
                        </span>
                      </div>
                    )}
                    {/* Status badges row */}
                    <div className="flex items-center gap-1.5 flex-wrap mb-2">
                      <Badge className={`${colors.bg} ${colors.text} border ${colors.border} text-[11px] px-2 py-0.5 whitespace-nowrap`}>
                        <div className={`w-1.5 h-1.5 rounded-full ${colors.dot} mr-1.5`} />
                        {displayStatus}
                      </Badge>
                      {/* Pharmacy posted badge — only shown when locally
                          completed AND the prescription was posted (which is
                          extra context the unified display_status doesn't
                          imply). The plain "Completed" status is now covered
                          by the main status badge. */}
                      {appt.consultation_status === 'completed' && appt.pharmacy_posted && (
                        <Badge
                          className="bg-emerald-100 text-emerald-800 border border-emerald-300 text-[11px] px-2 py-0.5 gap-1 whitespace-nowrap"
                          data-testid={`completed-badge-${i}`}
                          title={`Prescription posted: ${appt.pharmacy_order_number || ''}`}
                        >
                          <CheckCircle className="h-3 w-3" /> Rx Posted
                        </Badge>
                      )}
                    </div>

                    {/* Patient name with salutation - proper case */}
                    <h3 className="text-base font-bold text-gray-900 mb-0.5 group-hover:text-blue-800 transition-colors" style={{ fontFamily: 'Manrope' }}>
                      {appt.salutation ? `${appt.salutation} ` : ''}{toProperCase(appt.patient_name)}
                    </h3>

                    {/* Age / Gender inline */}
                    {(appt.age || appt.gender) && (
                      <p className="text-xs text-gray-500 mb-1.5">
                        {appt.age ? `${appt.age} yrs` : ''}{appt.age && appt.gender ? ' / ' : ''}{appt.gender || ''}
                      </p>
                    )}

                    {/* Details */}
                    <div className="space-y-1 text-sm text-gray-600">
                      {appt.appointment_id && (
                        <div className="flex items-center gap-2">
                          <FileText className="h-3.5 w-3.5 text-blue-500" />
                          <span className="font-medium text-blue-700">OP#: {appt.appointment_id}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <Hash className="h-3.5 w-3.5 text-gray-400" />
                        <span>MRN: {appt.mrn}</span>
                      </div>
                      {appt.phone && (
                        <div className="flex items-center gap-2">
                          <Phone className="h-3.5 w-3.5 text-gray-400" />
                          <span>{appt.phone}</span>
                        </div>
                      )}
                      {appt.specialisation && (
                        <div className="flex items-center gap-2">
                          <Stethoscope className="h-3.5 w-3.5 text-gray-400" />
                          <span>{appt.specialisation}</span>
                        </div>
                      )}
                      {appt.referral_doctor && (
                        <div className="flex items-center gap-2">
                          <UserCheck className="h-3.5 w-3.5 text-orange-500" />
                          <span className="text-orange-700 font-medium">Ref: {formatDoctorName(toProperCase(appt.referral_doctor.name))}{appt.referral_doctor.type ? ` (${appt.referral_doctor.type})` : ''}</span>
                        </div>
                      )}
                    </div>

                    {/* Tags grouped logically */}
                    <div className="mt-2 space-y-1.5" data-testid={`tags-${i}`}>
                      {/* Group 1: Visit context (type · appointment · scheme) */}
                      <div className="flex flex-wrap items-center gap-1.5">
                        <Badge variant="outline" className="text-[11px] px-2 py-0.5 font-semibold">{appt.visit_type}</Badge>
                        {appt.appointment_type && !appt.appointment_type.toLowerCase().includes('new visit') && (
                          <Badge variant="outline" className="text-[11px] px-2 py-0.5 text-indigo-700 border-indigo-200 bg-indigo-50" data-testid={`appt-type-${i}`}>
                            {toProperCase(appt.appointment_type)}
                          </Badge>
                        )}
                        {appt.scheme && (() => {
                          // Match the tier color-coding used on the
                          // consultation header so the same patient looks
                          // visually consistent across dashboard → consult.
                          const TIERS = {
                            platinum:  'bg-purple-100 text-purple-800',
                            diamond:   'bg-cyan-100 text-cyan-800',
                            gold:      'bg-amber-100 text-amber-800',
                            silver:    'bg-slate-200 text-slate-800',
                            bronze:    'bg-orange-100 text-orange-800',
                            insurance: 'bg-blue-100 text-blue-800',
                            esic:      'bg-red-100 text-red-700',
                            cmchs:     'bg-emerald-100 text-emerald-800',
                            tnnhis:    'bg-emerald-100 text-emerald-800',
                          };
                          const cls = TIERS[appt.scheme.trim().toLowerCase()] || 'bg-teal-100 text-teal-700';
                          return (
                            <Badge className={`${cls} text-[11px] px-2 py-0.5 border-0 gap-0.5 font-semibold`} data-testid={`scheme-${i}`}>
                              <Shield className="h-3 w-3" /> {appt.scheme}
                            </Badge>
                          );
                        })()}
                      </div>

                      {/* Group 2: Status (vitals + payment) */}
                      <div className="flex flex-wrap items-center gap-1.5">
                        {appt.vital_status && (
                          <Badge className={`text-[11px] px-2 py-0.5 border-0 gap-0.5 ${
                            appt.vital_status === 'completed' ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-700'
                          }`} data-testid={`vital-${i}`}>
                            <Activity className="h-3 w-3" />
                            Vitals {appt.vital_status === 'completed' ? 'Done' : 'Pending'}
                          </Badge>
                        )}
                        {appt.payment_done ? (
                          <Badge className="bg-green-100 text-green-700 text-[11px] px-2 py-0.5 border-0 gap-0.5" data-testid={`paid-${i}`}>
                            <CreditCard className="h-3 w-3" /> Paid
                          </Badge>
                        ) : (
                          <Badge className="bg-orange-100 text-orange-700 text-[11px] px-2 py-0.5 border-0 gap-0.5" data-testid={`unpaid-${i}`}>
                            <CreditCard className="h-3 w-3" /> Unpaid
                          </Badge>
                        )}
                      </div>

                      {/* Group 3: Patient novelty (only if present) */}
                      {(appt.is_new_hospital || appt.is_new_doctor || appt.has_tumor_board) && (
                        <div className="flex flex-wrap items-center gap-1.5">
                          {appt.is_new_hospital && (
                            <Badge className="bg-purple-100 text-purple-700 text-[11px] px-2 py-0.5 border-0 gap-0.5" data-testid={`new-patient-${i}`}>
                              <UserPlus className="h-3 w-3" /> New Hospital Visit
                            </Badge>
                          )}
                          {appt.is_new_doctor && (
                            <Badge className="bg-sky-100 text-sky-700 text-[11px] px-2 py-0.5 border-0 gap-0.5" data-testid={`first-visit-${i}`}>
                              <UserCheck className="h-3 w-3" /> New Patient for Doctor
                            </Badge>
                          )}
                          {appt.has_tumor_board && (
                            <Badge className="bg-rose-100 text-rose-700 text-[11px] px-2 py-0.5 border-0 gap-0.5" data-testid={`tumor-board-${i}`} title="This visit was discussed at the Tumor Board">
                              <Microscope className="h-3 w-3" /> Tumor Board
                            </Badge>
                          )}
                        </div>
                      )}

                      {/* Visits by patient + last-visited (clickable drawer when prescription was captured) */}
                      {(appt.last_visit_by_any || appt.total_prior_visits > 0) && (
                        <div className="flex flex-wrap items-center gap-1.5">
                          {appt.total_prior_visits > 0 && (
                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 border border-emerald-200 rounded-full text-[11px] font-semibold text-emerald-800"
                              data-testid={`card-total-visits-${i}`}
                            >
                              <Activity className="h-3 w-3" /> Visits by Patient: {appt.total_prior_visits}
                            </span>
                          )}
                          {appt.last_visit_by_any && (() => {
                            const lva = appt.last_visit_by_any;
                            const hasRx = !!lva.consultation_id;
                            const label = (
                              <>
                                <Clock className="h-3 w-3" /> Last Visited: {formatDoctorName(lva.doctor_name || 'Unknown')}
                                {lva.visit_type ? ` · ${lva.visit_type}` : ''} · {formatDateTimeIST(lva.visit_date)}
                              </>
                            );
                            return hasRx ? (
                              <span
                                role="button"
                                tabIndex={0}
                                onClick={(e) => { e.stopPropagation(); setPastVisitId(lva.consultation_id); setPastVisitTitle(`Last visit · ${formatDoctorName(lva.doctor_name)}`); }}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); e.preventDefault(); setPastVisitId(lva.consultation_id); setPastVisitTitle(`Last visit · ${formatDoctorName(lva.doctor_name)}`); } }}
                                className="inline-flex items-center gap-1 px-2 py-0.5 bg-violet-50 hover:bg-violet-100 border border-violet-200 rounded-full text-[11px] font-semibold text-violet-800 transition-colors cursor-pointer"
                                data-testid={`card-last-visited-${i}`}
                                title="Click to view full prescription details"
                              >
                                {label}
                              </span>
                            ) : (
                              <span
                                className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-50 border border-gray-200 rounded-full text-[11px] font-semibold text-gray-600"
                                data-testid={`card-last-visited-${i}`}
                                title="Visit recorded in EMR — no detailed prescription captured here"
                              >
                                {label}
                              </span>
                            );
                          })()}
                        </div>
                      )}
                    </div>

                    {/* Footer arrow */}
                    <div className="flex justify-end mt-2">
                      <ChevronRight className="h-4 w-4 text-gray-300 group-hover:text-blue-600 transition-colors" />
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
        </>
        )}
      </div>

      {/* Purchase Requests Slide-out (Doctor Read-Only View) */}
      {showPurchaseRequests && (() => {
        const prq = prSearch.toLowerCase();
        const filtered = prq
          ? purchaseRequests.filter(pr => pr.medicine_name.toLowerCase().includes(prq) || (pr.composition || '').toLowerCase().includes(prq))
          : purchaseRequests;
        const display = filtered.slice(0, 10);
        return (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="purchase-requests-panel">
          <div className="absolute inset-0 bg-black/30" onClick={() => setShowPurchaseRequests(false)} />
          <div className="relative w-full max-w-lg bg-white shadow-2xl flex flex-col animate-in slide-in-from-right">
            <div className="px-5 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-white flex-shrink-0 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ShoppingCart className="h-5 w-5 text-blue-700" />
                  <h2 className="text-base font-bold text-gray-900" style={{ fontFamily: 'Manrope' }}>My Purchase Requests</h2>
                  <Badge className="bg-blue-100 text-blue-700 text-xs border-0">{purchaseRequests.filter(pr => pr.status === 'pending').length} pending</Badge>
                </div>
                <button onClick={() => setShowPurchaseRequests(false)} className="p-1 rounded hover:bg-gray-100">
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                <input type="text" value={prSearch} onChange={(e) => setPrSearch(e.target.value)}
                  placeholder="Search by medicine name..."
                  className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  data-testid="pr-search-input" />
              </div>
            </div>
            <div className="flex-1 overflow-auto p-4">
              {prLoading ? (
                <div className="text-center py-12 text-gray-400 text-sm">Loading...</div>
              ) : display.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <Package className="h-10 w-10 mx-auto mb-3 opacity-30" />
                  <p className="text-sm font-medium">{prSearch ? 'No matching requests' : 'No purchase requests'}</p>
                  <p className="text-xs mt-1">Low stock & out-of-stock medicine requests will appear here</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">
                    Showing {display.length} of {filtered.length} request{filtered.length !== 1 ? 's' : ''}
                  </p>
                  {display.map((pr) => (
                    <div key={pr.id} className={`p-3 rounded-lg border ${
                      pr.status === 'pending' ? 'border-amber-200 bg-amber-50/50' :
                      pr.status === 'ordered' ? 'border-blue-200 bg-blue-50/50' :
                      pr.status === 'received' ? 'border-green-200 bg-green-50/50' :
                      'border-gray-200 bg-gray-50/50'
                    }`} data-testid={`pr-item-${pr.id}`}>
                      <div className="flex items-start justify-between">
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-sm text-gray-900">{pr.medicine_name}</p>
                          {pr.composition && <p className="text-[11px] text-blue-600 mt-0.5 truncate">{pr.composition}</p>}
                          <div className="flex items-center gap-3 mt-1.5 text-[11px] text-gray-500">
                            <span>{formatDoctorName(pr.doctor_name)}</span>
                            {pr.patient_name && <span>Patient: {pr.patient_name}</span>}
                            <span>{pr.created_at ? formatDateIST(pr.created_at) : ''}</span>
                          </div>
                        </div>
                        <Badge className={`text-[10px] border-0 flex-shrink-0 ${
                          pr.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                          pr.status === 'ordered' ? 'bg-blue-100 text-blue-700' :
                          pr.status === 'received' ? 'bg-green-100 text-green-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>{pr.status}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        );
      })()}

      {/* Expiring Stock Alert Drawer */}
      {showExpiringDrawer && (() => {
        const items = expiringSummary?.items || [];
        const q = expiringSearch.trim().toLowerCase();
        const filtered = q ? items.filter(it =>
          (it.item_name || '').toLowerCase().includes(q) ||
          (it.manufacturer || '').toLowerCase().includes(q) ||
          (it.brand || '').toLowerCase().includes(q)
        ) : items;
        return (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="expiring-drawer">
          <div className="absolute inset-0 bg-black/30" onClick={() => setShowExpiringDrawer(false)} />
          <div className="relative w-full max-w-2xl bg-white shadow-2xl flex flex-col animate-in slide-in-from-right">
            <div className="px-5 py-4 border-b border-gray-200 bg-gradient-to-r from-amber-50 to-white flex-shrink-0 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-700" />
                  <h2 className="text-base font-bold text-gray-900" style={{ fontFamily: 'Manrope' }}>Expiring Stock Alert</h2>
                  <Badge className="bg-red-100 text-red-700 text-xs border-0">{expiringSummary?.expired_count || 0} expired</Badge>
                  <Badge className="bg-amber-100 text-amber-800 text-xs border-0">{expiringSummary?.expiring_count || 0} expiring ≤ {expiringSummary?.window_days || 30}d</Badge>
                </div>
                <button onClick={() => setShowExpiringDrawer(false)} className="p-1 rounded hover:bg-gray-100" data-testid="expiring-close">
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                <input type="text" value={expiringSearch} onChange={(e) => setExpiringSearch(e.target.value)}
                  placeholder="Search item, brand or manufacturer..."
                  className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  data-testid="expiring-search-input" />
              </div>
            </div>
            <div className="flex-1 overflow-auto p-4">
              {expiringLoading ? (
                <div className="text-center py-12 text-gray-400 text-sm">Loading...</div>
              ) : filtered.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <Package className="h-10 w-10 mx-auto mb-3 opacity-30" />
                  <p className="text-sm font-medium">{q ? 'No matches' : 'No expiring or expired stock'}</p>
                  <p className="text-xs mt-1">All pharmacy batches are healthy — nothing expiring in the next {expiringSummary?.window_days || 30} days.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">
                    Showing {filtered.length} item{filtered.length !== 1 ? 's' : ''}
                  </p>
                  {filtered.map((it) => {
                    const isExpired = it.status === 'expired';
                    return (
                    <div key={it.item_code} className={`p-3 rounded-lg border ${
                      isExpired ? 'border-red-200 bg-red-50/40' : 'border-amber-200 bg-amber-50/40'
                    }`} data-testid={`expiring-item-${it.item_code}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="font-semibold text-sm text-gray-900">{it.item_name}</p>
                          <div className="flex items-center gap-3 mt-1 text-[11px] text-gray-500 flex-wrap">
                            {it.manufacturer && <span>{it.manufacturer}</span>}
                            <span>Total stock: <span className="font-semibold text-gray-700">{it.total_stock}</span></span>
                            <span>{it.batches.length} batch{it.batches.length !== 1 ? 'es' : ''}</span>
                          </div>
                        </div>
                        <Badge className={`text-[10px] border-0 flex-shrink-0 ${
                          isExpired ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-800'
                        }`}>
                          {isExpired
                            ? `Expired ${Math.abs(it.nearest_days_left)}d ago`
                            : `${it.nearest_days_left}d left`}
                        </Badge>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {it.batches.slice(0, 6).map((b, i) => (
                          <span key={i}
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ${
                              b.status === 'expired' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-800'
                            }`}
                            title={`Batch ${b.batch_number || '—'} · ${b.expiry_date.slice(0,10)} · stock ${b.stock}`}>
                            {b.batch_number || '—'} · {b.expiry_date.slice(0,10)} · {b.stock}
                          </span>
                        ))}
                        {it.batches.length > 6 && (
                          <span className="text-[10px] text-gray-500">+{it.batches.length - 6} more</span>
                        )}
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
        );
      })()}

      {/* Past visit drawer (opened from card chips) */}
      {pastVisitId && (
        <PastVisitDrawer
          consultationId={pastVisitId}
          title={pastVisitTitle}
          onClose={() => { setPastVisitId(null); setPastVisitTitle(''); }}
        />
      )}

      {/* IP card drawer — read-only view of an admitted/discharged
          patient's complete record (sections, prescriptions, tests,
          discharge summary). Drives off `appointment_id` resolved
          server-side from the IPNumber → patient_appointment mapping. */}
      {ipDrawerApptId && (
        <AppointmentDetailDrawer
          appointmentId={ipDrawerApptId}
          isCurrentDoctor={false}
          onClose={() => setIpDrawerApptId(null)}
        />
      )}
    </div>
  );
}
